import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useOrg } from '../../contexts/OrgContext';
import { useAuth } from '../../contexts/AuthContext';
import { getFloc, getFlocs, updateFloc } from '../../services/flocService';
import { FlocType, Criticality, FunctionalLocation } from '@easydone/shared';
import { FlocTreeSelect } from '../../components/FlocTreeSelect';

export function EditFlocPage() {
  const { flocId } = useParams<{ flocId: string }>();
  const { currentOrg } = useOrg();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [flocs, setFlocs] = useState<FunctionalLocation[]>([]);
  const [parentFlocId, setParentFlocId] = useState<string>('');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<FlocType>('equipment_position');
  const [criticality, setCriticality] = useState<Criticality>('medium');
  const [functionalSystem, setFunctionalSystem] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, [currentOrg, flocId]);

  const loadData = async () => {
    if (!currentOrg || !flocId) return;

    setLoading(true);
    try {
      const [flocData, flocsData] = await Promise.all([
        getFloc(currentOrg.id, flocId),
        getFlocs(currentOrg.id)
      ]);

      if (!flocData) {
        setError('FLOC not found');
        return;
      }

      // Populate form
      setCode(flocData.code);
      setName(flocData.name);
      setDescription(flocData.description || '');
      setType(flocData.type);
      setCriticality(flocData.criticality);
      setFunctionalSystem(flocData.functionalSystem || '');
      setParentFlocId(flocData.parentFlocId || '');
      setFlocs(flocsData);
    } catch (err) {
      console.error('Failed to load FLOC:', err);
      setError('Failed to load FLOC data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentOrg || !currentUser || !flocId) return;

    setSaving(true);
    setError('');

    try {
      // Build update data, omitting undefined values
      const updateData: any = {
        code,
        name,
        type,
        criticality,
      };
      if (description) updateData.description = description;
      if (functionalSystem) updateData.functionalSystem = functionalSystem;
      if (parentFlocId) updateData.parentFlocId = parentFlocId;

      await updateFloc(currentOrg.id, flocId, updateData, currentUser.uid);

      navigate(`/flocs/${flocId}`);
    } catch (err: any) {
      setError(err.message || 'Failed to update functional location');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Edit Functional Location</h1>
        <p className="text-gray-600 mt-1">
          Update the details of this location
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="card space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Parent Location (Optional)
          </label>
          <div className="mb-2">
            <button
              type="button"
              onClick={() => setParentFlocId('')}
              className={`btn text-sm ${
                parentFlocId === '' ? 'btn-primary' : 'btn-secondary'
              }`}
            >
              None (Top Level)
            </button>
          </div>
          {flocs.length > 1 && (
            <>
              <p className="text-xs text-gray-600 mb-2">
                Or select a parent from the hierarchy:
              </p>
              <FlocTreeSelect
                flocs={flocs}
                selectedId={parentFlocId}
                onSelect={(id) => setParentFlocId(id)}
                excludeId={flocId}
              />
            </>
          )}
          <p className="text-xs text-gray-500 mt-2">
            You cannot set this FLOC as its own parent or descendant
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            FLOC Code *
          </label>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="e.g., SITE-A/BLDG-1/PUMP-01"
            className="input w-full"
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            Hierarchical code identifying this location
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Name *
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Main Cooling Pump"
            className="input w-full"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Additional details about this location..."
            className="input w-full"
            rows={3}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Type *
          </label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value as FlocType)}
            className="input w-full"
            required
          >
            <option value="site">Site</option>
            <option value="building">Building</option>
            <option value="floor">Floor</option>
            <option value="area">Area</option>
            <option value="zone">Zone</option>
            <option value="system">System</option>
            <option value="equipment_position">Equipment Position</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Functional System
          </label>
          <input
            type="text"
            value={functionalSystem}
            onChange={(e) => setFunctionalSystem(e.target.value)}
            placeholder="e.g., HVAC, Electrical, Water Treatment"
            className="input w-full"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Criticality *
          </label>
          <select
            value={criticality}
            onChange={(e) => setCriticality(e.target.value as Criticality)}
            className="input w-full"
            required
          >
            <option value="not_in_use">Not in use - Asset not installed or operational</option>
            <option value="very_low">Very low - Minimal impact if unavailable</option>
            <option value="low">Low - Minor impact if unavailable</option>
            <option value="medium">Medium - Moderate impact</option>
            <option value="high">High - Significant impact</option>
            <option value="very_high">Very high - Operations cannot continue</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">
            How important is this location to operations?
          </p>
        </div>

        <div className="flex justify-between pt-4 border-t">
          <button
            type="button"
            onClick={() => navigate(`/flocs/${flocId}`)}
            className="btn btn-secondary"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
}
